import { Card, CardContent } from "@/components/ui/card";
import { Star, MapPin, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

interface TestimonialCardProps {
  name: string;
  country: string;
  date: string;
  rating: number;
  comment: string;
  compact?: boolean;
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getAvatarColor = (name: string) => {
  const colors = [
    'bg-roca-yellow/20 text-roca-yellow border-roca-yellow',
    'bg-roca-red/20 text-roca-red border-roca-red',
    'bg-primary/20 text-primary border-primary',
    'bg-accent/20 text-accent border-accent',
    'bg-roca-orange/20 text-roca-orange border-roca-orange',
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

export const TestimonialCard = ({ name, country, date, rating, comment, compact = false }: TestimonialCardProps) => {
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
      />
    ));
  };

  const displayComment = compact && comment.length > 80 
    ? comment.slice(0, 80) + '...' 
    : comment;

  const cardContent = (
    <Card className="shadow-card hover:shadow-float transition-smooth h-full border-l-4 border-primary/30">
      <CardContent className="p-6">
        {/* Header avec avatar et infos */}
        <div className="flex items-start gap-4 mb-4">
          {/* Avatar avec initiales */}
          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg border-2 flex-shrink-0 ${getAvatarColor(name)}`}>
            {getInitials(name)}
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Nom */}
            <h3 className="font-semibold text-lg text-foreground truncate">{name}</h3>
            
            {/* Localisation et date */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-1">
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span>{country}</span>
              </div>
              {!compact && (
                <>
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{date}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Étoiles */}
        <div className="flex gap-1 mb-3">
          {renderStars(rating)}
        </div>
        
        {/* Commentaire */}
        <p className="text-sm text-muted-foreground leading-relaxed italic">
          "{displayComment}"
        </p>
      </CardContent>
    </Card>
  );

  if (compact) {
    return (
      <Link to="/avis" className="block hover:scale-[1.02] transition-transform">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
};